import {
  Box,
  Typography,
  TextField,
  InputBase,
  IconButton,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export const LanguageListSection = styled(Box)({
  padding: 0,
});

export const LanguageListHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  flexWrap: 'wrap',
  gap: 8,
});

export const LanguageListCountText = styled(Typography)({
  fontSize: '11px',
  fontWeight: 400,
  textTransform: 'none' as const,
  color: 'var(--am-text-secondary)',
}) as typeof Typography;

export const LanguageRowsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

// Wraps LanguageRow (the selectable/highlighted part) and TranslateRowButton
// as siblings, so the row's selection frame only ever outlines the
// flag/name/field — the translate button sits outside it and never reads as
// part of the selection state.
export const LanguageRowOuter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
});

export const LanguageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flex: 1,
  minWidth: 0,
  padding: '5px 8px',
  margin: '0 -8px',
  borderRadius: 6,
  cursor: 'pointer',
  backgroundColor: $selected ? 'var(--am-accent-surface)' : 'transparent',
  boxShadow: $selected ? 'inset 0 0 0 1px var(--am-accent-text)' : 'none',
  transition: 'background-color 120ms, box-shadow 120ms',
}));

export const LanguageRowFlag = styled('span')({
  fontSize: 16,
  flexShrink: 0,
});

export const LanguageRowName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$base',
})<{ $base?: boolean }>(({ $base }) => ({
  width: 88,
  flexShrink: 0,
  fontSize: 13,
  fontWeight: $base ? 600 : 400,
  color: $base ? 'var(--am-success-color)' : 'var(--am-text-secondary)',
}));

export const BaseInputWrap = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 6px 0 12px',
  borderRadius: $selected ? 0 : 4,
  // Without this, the input's own background/focus box can render past this
  // wrap's rounded corners, making the green tint look like it "bleeds" out
  // of its box instead of staying contained the way TranslationField does.
  overflow: 'hidden',
  border: $selected ? 'none' : '1px solid var(--am-success-color)',
  borderBottom: $selected ? '1px solid var(--am-success-color)' : undefined,
  backgroundColor: $selected ? 'transparent' : 'var(--am-success-bg)',
}));

export const BaseInput = styled(InputBase)({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: 'var(--am-text-primary)',
  // The dialog-wide `.MuiDialog-paper .MuiInputBase-root` rule (2 classes,
  // specificity 0,2,0) always beats this single-class override (0,1,0) on
  // plain CSS specificity, regardless of style-tag order — so `!important`
  // is required, not optional. Without it, that rule's background paints
  // over BaseInputWrap's green tint wherever the input itself sits, leaving
  // only the wrap's padding showing the correct tint on the left/right.
  backgroundColor: 'transparent !important',
  '& .MuiInputBase-input': {
    padding: '8px 0',
  },
});

export const BaseBadge = styled(Typography)({
  flexShrink: 0,
  fontSize: '9px',
  fontWeight: 700,
  padding: '2px 6px',
  marginRight: 8,
  borderRadius: 4,
  backgroundColor: 'var(--am-icon-accent)',
  color: 'var(--am-primary-btn-color)',
  lineHeight: 1.4,
}) as typeof Typography;

// A muted amber, not the shared --am-warning-* tokens (that palette leans
// red/alarm — too harsh for a soft "this might be outdated" nudge).
export const STALE_BORDER_COLOR = '#c9973f';
export const STALE_BG_COLOR = 'rgba(201,151,63,0.12)';

export const TranslationField = styled(TextField)({
  flex: 1,
  minWidth: 0,
  '& .MuiInputBase-root': {
    fontSize: 14,
  },
});

export const TranslateRowButton = styled(IconButton)({
  flexShrink: 0,
  width: 32,
  height: 32,
  marginRight: 6,
  borderRadius: 8,
  fontSize: 14,
  color: 'var(--am-ghost-btn-color)',
  border: 'var(--am-ghost-btn-border)',
  '&:hover': {
    backgroundColor: 'var(--am-ghost-btn-hover-bg)',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
});

export const ShowMoreButton = styled(Button)({
  width: '100%',
  marginTop: 8,
  justifyContent: 'center',
  gap: 4,
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: 10,
  padding: '8px 0',
  color: 'var(--am-text-secondary)',
  border: '1px solid var(--am-border-mid)',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'var(--am-surface-2)',
    border: '1px solid var(--am-border-mid)',
  },
});

export const NoaPanelPreview = styled(Box)({
  borderRadius: 12,
  backgroundColor: 'var(--am-preview-panel-bg)',
  padding: '12px 8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
  marginTop: 16,
});

export const NoaPanelPreviewLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: 'rgba(255,255,255,0.35)',
}) as typeof Typography;

export const NoaPanelPreviewButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isFallback',
})<{ $isFallback?: boolean }>(({ $isFallback }) => ({
  border: '2px solid rgba(255,255,255,0.7)',
  borderRadius: '50px',
  padding: '12px 40px',
  color: 'white',
  fontWeight: 600,
  fontSize: '15px',
  minWidth: 220,
  textAlign: 'center' as const,
  backgroundColor: '#4B52D4',
  opacity: $isFallback ? 0.55 : 1,
}));

export const NoTranslationCaption = styled(Typography)({
  fontSize: '11px',
  color: 'rgba(255,255,255,0.4)',
}) as typeof Typography;
